import { useState, useCallback, useRef } from "react";
import { uploadAsset } from "../../services/assetsApi";
import { config } from "../../config/environment";
import { VIDEO_MIME_TYPES, VIDEO_EXTENSIONS, getAcceptString, hasValidExtension } from "../../utils/fileTypes";
import {
  Box,
  Button,
  Icon,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { MdUpload, MdVideocam } from "react-icons/md";
import { useParams } from "react-router-dom";
import { useRoomStore, usePlacedModelsStore, useUserInterfaceStore, useModelStore } from "../../store";
import { flushEditorSave } from "../../pages/EditorPage";
import { getDecorationPlacementForCamera } from "../../constants/Helpers";
import { track, Events } from "../../utils/analytics";

const MAX_FILE_SIZE = config.upload.maxFileSize.wallVideo;
const MAX_FILE_SIZE_MB = Math.round(MAX_FILE_SIZE / (1024 * 1024));

export default function VideoUploadForDecoration({ setSelectedValue }) {
  const { roomId } = useParams();
  const fileInputRef = useRef(null);

  const [uploading, setUploading] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState(null);
  const [error, setError] = useState(null);
  const [codecWarning, setCodecWarning] = useState(null);

  const scaleX = useRoomStore((s) => s.scaleX);
  const scaleZ = useRoomStore((s) => s.scaleZ);
  const height = useRoomStore((s) => s.height);
  const addDecoration = useRoomStore((s) => s.addDecoration);
  const setSelectedDecoration = useUserInterfaceStore((s) => s.setSelectedDecoration);
  const cameraControls = useModelStore((s) => s.cameraControls);

  const checkCodecCompatibility = useCallback((file) => {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const video = document.createElement("video");
      video.muted = true;

      const cleanup = () => URL.revokeObjectURL(url);

      video.onerror = () => {
        cleanup();
        resolve(video.error?.code === 4); // code 4 = unsupported codec
      };

      video.oncanplay = () => {
        cleanup();
        resolve(false); // browser can play it fine
      };

      // Timeout fallback — treat as compatible if no answer in 1s
      setTimeout(() => { cleanup(); resolve(false); }, 1000);

      video.src = url;
      video.load();
    });
  }, []);

  const handleFileSelect = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate file type — using canonical VIDEO_MIME_TYPES from fileTypes.js
      const fileName = file.name.toLowerCase();
      const hasValidMime = VIDEO_MIME_TYPES.includes(file.type);
      const extOk = hasValidExtension(file.name, 'video');

      if (!hasValidMime && !extOk) {
        setError("Only MP4, MOV, and M4V videos are supported");
        return;
      }

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        setError(`File too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`);
        return;
      }

      // Check if this browser can play the video — catches HEVC/unsupported codecs
      const unsupported = await checkCodecCompatibility(file);
      if (unsupported) {
        setCodecWarning("This video likely won't play on Chrome — it uses an unsupported codec (e.g. HEVC/H.265). Re-export as H.264 MP4 for best compatibility.");
      } else {
        setCodecWarning(null);
      }

      setUploading(true);
      setError(null);

      try {
        const result = await uploadAsset(file, { roomId });

        if (result.success) {
          const videoUrl = result.data.publicUrl || result.data.url;
          setUploadedUrl(videoUrl);

          // Set the decoration option
          if (setSelectedValue) {
            setSelectedValue(videoUrl);
          }

          // Auto-create decoration
          const defaultWidth = 30;
          const defaultHeight = defaultWidth * (9 / 16); // 16:9 aspect ratio

          const randomX = (Math.random() - 0.5) * 30;
          const randomY = (Math.random() - 0.5) * 20;

          const placement = getDecorationPlacementForCamera(
            cameraControls, scaleX, scaleZ, height, randomX, randomY
          );

          const decoration = {
            type: "video",
            option: videoUrl,
            assetKey: result.data.assetKey, // stored so URL can be refreshed when room loads
            position: placement.position,
            rotation: placement.rotation,
            width: defaultWidth,
            height: defaultHeight,
            link: "",
            hoverText: "",
          };

          const currentDecorationsVideo = useRoomStore.getState().decorations;
          const isFirstDecorationVideo = currentDecorationsVideo.length === 0;
          const roomIdVideo = usePlacedModelsStore.getState()._roomId;

          console.log("🎬 Auto-creating video decoration:", decoration);
          addDecoration(decoration);

          if (isFirstDecorationVideo) {
            track(Events.DECORATION_ADDED, { roomId: roomIdVideo, decorationType: 'video' });
          }

          // Flush after React re-renders so the auto-save effect has queued
          // the debounced call with the new decoration data
          setTimeout(() => flushEditorSave(), 0);

          // Select the newly created decoration
          setTimeout(() => {
            const newIndex = useRoomStore.getState().decorations.length - 1;
            console.log("🎯 Selecting new video decoration at index:", newIndex);
            setSelectedDecoration(decoration, newIndex);
          }, 50);

        } else {
          setError(result.error?.message || "Upload failed");
        }
      } catch (err) {
        setError(err.message || "Upload failed");
      } finally {
        setUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    },
    [roomId, setSelectedValue, scaleX, scaleZ, height, addDecoration, setSelectedDecoration, cameraControls, checkCodecCompatibility]
  );

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box my="10px" background="#1C1C1C">
      <input
        ref={fileInputRef}
        type="file"
        accept={getAcceptString('video')}
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />

      {uploadedUrl ? (
        <VStack spacing={3} align="stretch">
          <Box
            position="relative"
            bg="gray.900"
            borderRadius="0"
            overflow="hidden"
            border="2px solid"
            borderColor="gray.600"
          >
            <video
              src={uploadedUrl}
              style={{
                width: "100%",
                maxHeight: "150px",
                objectFit: "contain",
              }}
              muted
              loop
              autoPlay
              playsInline
            />
            <Box
              position="absolute"
              top={2}
              right={2}
              bg="blackAlpha.700"
              borderRadius="0"
              p={1}
            >
              <Icon as={MdVideocam} color="white" boxSize={4} />
            </Box>
          </Box>
          <Button
            size="sm"
            onClick={triggerFileSelect}
            isLoading={uploading}
            loadingText="Uploading..."
            bg="gray.700"
            _hover={{ bg: "gray.600" }}
            borderRadius="0"
          >
            <Icon as={MdUpload} mr={1} />
            Upload Another
          </Button>
        </VStack>
      ) : (
        <VStack spacing={3} align="stretch">
          <Button
            onClick={triggerFileSelect}
            isLoading={uploading}
            loadingText="Uploading..."
            size="md"
            height="80px"
            bg="gray.800"
            border="2px dashed"
            borderColor="gray.600"
            _hover={{ bg: "gray.700", borderColor: "gray.500" }}
            borderRadius="0"
          >
            <VStack spacing={1}>
              {uploading ? (
                <Spinner size="md" />
              ) : (
                <>
                  <Icon as={MdUpload} boxSize={6} />
                  <Text fontSize="sm">Upload Video</Text>
                </>
              )}
            </VStack>
          </Button>
          <Text fontSize="xs" color="gray.500" textAlign="center">
            MP4 up to {MAX_FILE_SIZE_MB}MB. "High efficiency" iPhone videos don't work on Chrome desktop.
          </Text>
        </VStack>
      )}

      {codecWarning && (
        <Text fontSize="xs" color="orange.300" mt={2}>
          ⚠️ {codecWarning}
        </Text>
      )}

      {error && (
        <Text fontSize="xs" color="red.400" mt={2}>
          {error}
        </Text>
      )}
    </Box>
  );
}
